import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TrackerDataSource } from '@app/core/constants/global-enum';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface TrackerSourceIndicatorViewModel {
    icon: IconProp;
    label: string;
    className: string;
}

@Component({
    selector: 'app-tracker-source-indicator',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ng-container *ngIf="indicator as item">
            <span
                class="inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] leading-none ring-1"
                [ngClass]="item.className"
                role="img"
                [attr.title]="item.label"
                [attr.aria-label]="item.label">
                <fa-icon [icon]="item.icon" class="text-[10px] leading-none"></fa-icon>
            </span>
        </ng-container>
    `,
    host: {
        class: 'inline-flex'
    }
})
export class TrackerSourceIndicatorComponent {
    @Input() value: number | string | null | undefined;

    get indicator(): TrackerSourceIndicatorViewModel | null {
        switch (this.normalize(this.value)) {
            case TrackerDataSource.Simulator:
                return {
                    icon: ['fal', 'robot'] as IconProp,
                    label: 'Simulator',
                    className: 'bg-violet-50 text-violet-700 ring-violet-200'
                };
            case TrackerDataSource.Offline:
                return {
                    icon: ['fal', 'wifi-slash'] as IconProp,
                    label: 'Offline',
                    className: 'bg-slate-50 text-slate-700 ring-slate-200'
                };
            case TrackerDataSource.Mixed:
                return {
                    icon: ['fal', 'random'] as IconProp,
                    label: 'Mixed',
                    className: 'bg-amber-50 text-amber-700 ring-amber-200'
                };
            case TrackerDataSource.Live:
            default:
                return null;
        }
    }

    private normalize(value: number | string | null | undefined): TrackerDataSource {
        if (value === null || value === undefined || value === '') {
            return TrackerDataSource.Live;
        }

        if (typeof value === 'number') {
            return this.toKnownSource(value);
        }

        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
            return this.toKnownSource(numericValue);
        }

        switch (value.trim().toLowerCase()) {
            case 'simulator':
            case 'simulation':
                return TrackerDataSource.Simulator;
            case 'offline':
                return TrackerDataSource.Offline;
            case 'mixed':
                return TrackerDataSource.Mixed;
            case 'live':
            default:
                return TrackerDataSource.Live;
        }
    }

    private toKnownSource(value: number): TrackerDataSource {
        switch (value) {
            case TrackerDataSource.Simulator:
            case TrackerDataSource.Offline:
            case TrackerDataSource.Mixed:
                return value;
            case TrackerDataSource.Live:
            default:
                return TrackerDataSource.Live;
        }
    }
}
